import { supabase } from './supabase';
import { twilioService } from './twilioService';

// Payment Recovery & Retention Service
export const paymentRecoveryService = {
  // Get failed payments dashboard data
  async getFailedPaymentsDashboard(gymId, filters = {}) {
    try {
      let query = supabase?.from('failed_payments')?.select(`
          *,
          user:user_profiles!user_id(id, full_name, email, phone),
          gym:gyms!gym_id(id, name),
          retry_schedules:payment_retry_schedule(count)
        `)?.eq('gym_id', gymId)?.order('created_at', { ascending: false });

      if (filters?.isResolved !== undefined) {
        query = query?.eq('is_resolved', filters?.isResolved);
      }

      if (filters?.dunningStage) {
        query = query?.eq('dunning_stage', filters?.dunningStage);
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Create failed payment record
  async createFailedPayment(paymentData) {
    try {
      const { data: failedPayment, error } = await supabase?.from('failed_payments')?.insert({
          user_id: paymentData?.userId,
          gym_id: paymentData?.gymId,
          subscription_plan: paymentData?.subscriptionPlan,
          amount: paymentData?.amount,
          currency: paymentData?.currency || 'USD',
          payment_method: paymentData?.paymentMethod,
          stripe_payment_intent_id: paymentData?.stripePaymentIntentId,
          failure_reason: paymentData?.failureReason,
          failure_code: paymentData?.failureCode,
          failure_message: paymentData?.failureMessage,
          member_value_score: paymentData?.memberValueScore || 0,
          payment_history_score: paymentData?.paymentHistoryScore || 0
        })?.select()?.single();

      if (error) throw error;

      // Schedule first retry attempt
      await this.schedulePaymentRetry(failedPayment?.id, 1);

      // Send initial dunning notification
      await this.sendDunningNotification(failedPayment?.id, 'initial');

      return { data: failedPayment, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Schedule payment retry
  async schedulePaymentRetry(failedPaymentId, retryAttempt) {
    try {
      const { data, error } = await supabase?.rpc('schedule_payment_retry', {
        failed_payment_uuid: failedPaymentId,
        retry_attempt_number: retryAttempt
      });

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Execute payment retry
  async executePaymentRetry(retryScheduleId) {
    try {
      // Get retry schedule details
      const { data: retrySchedule, error: scheduleError } = await supabase?.from('payment_retry_schedule')?.select(`
          *,
          failed_payment:failed_payments!failed_payment_id(
            *,
            user:user_profiles!user_id(id, full_name, email, stripe_customer_id)
          )
        `)?.eq('id', retryScheduleId)?.single();

      if (scheduleError) throw scheduleError;

      // Update retry status to in_progress
      await supabase?.from('payment_retry_schedule')?.update({ 
          retry_status: 'in_progress',
          executed_date: new Date()?.toISOString()
        })?.eq('id', retryScheduleId);

      // Attempt payment with Stripe (mock implementation)
      // In production, integrate with actual Stripe API
      const paymentSuccess = Math.random() > 0.4; // 60% success rate simulation

      if (paymentSuccess) {
        // Update retry schedule as succeeded
        await supabase?.from('payment_retry_schedule')?.update({ 
            retry_status: 'succeeded',
            result_message: 'Payment processed successfully'
          })?.eq('id', retryScheduleId);

        // Mark failed payment as resolved
        await supabase?.from('failed_payments')?.update({ 
            is_resolved: true,
            resolved_at: new Date()?.toISOString(),
            resolution_method: 'automatic_retry'
          })?.eq('id', retrySchedule?.failed_payment_id);

        // Send success notification
        await this.sendPaymentSuccessNotification(
          retrySchedule?.failed_payment?.user_id,
          retrySchedule?.amount_attempted
        );

        return { data: { success: true, message: 'Payment retry succeeded' }, error: null };
      } else {
        // Update retry schedule as failed
        await supabase?.from('payment_retry_schedule')?.update({ 
            retry_status: 'failed',
            result_message: 'Payment retry failed - card declined'
          })?.eq('id', retryScheduleId);

        // Check if max retries reached
        const { data: failedPayment } = await supabase?.from('failed_payments')?.select('retry_count, max_retry_attempts')?.eq('id', retrySchedule?.failed_payment_id)?.single();

        if (failedPayment?.retry_count >= failedPayment?.max_retry_attempts) {
          // Escalate dunning stage
          await this.escalateDunningStage(retrySchedule?.failed_payment_id);
        } else {
          // Schedule next retry
          await this.schedulePaymentRetry(
            retrySchedule?.failed_payment_id,
            failedPayment?.retry_count + 1
          );
        }

        return { data: { success: false, message: 'Payment retry failed' }, error: null };
      }
    } catch (error) {
      return { data: null, error };
    }
  },

  // Send dunning notification
  async sendDunningNotification(failedPaymentId, dunningStage) {
    try {
      // Get failed payment details
      const { data: failedPayment, error: paymentError } = await supabase?.from('failed_payments')?.select(`
          *,
          user:user_profiles!user_id(id, full_name, email, phone)
        `)?.eq('id', failedPaymentId)?.single();

      if (paymentError) throw paymentError;

      // Get dunning message template
      const messageTemplates = {
        initial: `Hi ${failedPayment?.user?.full_name}, your payment of $${failedPayment?.amount} failed. We will automatically retry in 24 hours. Please ensure your payment method is up to date.`,
        reminder_1: `Hi ${failedPayment?.user?.full_name}, this is a reminder that your payment of $${failedPayment?.amount} is still pending. We will retry again in 3 days. Update your payment method to avoid service interruption.`,
        reminder_2: `Hi ${failedPayment?.user?.full_name}, your payment of $${failedPayment?.amount} has failed multiple times. Please update your payment method immediately to maintain your membership.`,
        final_notice: `Hi ${failedPayment?.user?.full_name}, FINAL NOTICE: Your payment of $${failedPayment?.amount} is overdue. Your membership will be suspended if payment is not received within 48 hours. Contact us immediately.`,
        suspended: `Hi ${failedPayment?.user?.full_name}, your membership has been suspended due to non-payment. Please contact us to resolve this issue and reactivate your account.`
      };

      const message = messageTemplates?.[dunningStage] || messageTemplates?.initial;

      // Create dunning notification record
      const { data: notification, error: notificationError } = await supabase?.from('dunning_notifications')?.insert({
          failed_payment_id: failedPaymentId,
          user_id: failedPayment?.user_id,
          notification_type: 'sms',
          dunning_stage: dunningStage,
          message_template: message,
          message_sent: message,
          phone_number: failedPayment?.user?.phone,
          email_address: failedPayment?.user?.email
        })?.select()?.single();

      if (notificationError) throw notificationError;

      // Send SMS via Twilio
      if (failedPayment?.user?.phone) {
        const { data: smsResult, error: smsError } = await twilioService?.sendSMS(
          failedPayment?.user?.phone,
          message,
          'payment_dunning',
          'failed_payments',
          failedPaymentId
        );

        if (!smsError && smsResult?.logId) {
          // Update notification with SMS log reference
          await supabase?.from('dunning_notifications')?.update({ 
              sms_log_id: smsResult?.logId,
              sent_at: new Date()?.toISOString(),
              delivery_status: 'sent'
            })?.eq('id', notification?.id);
        }
      }

      return { data: notification, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Escalate dunning stage
  async escalateDunningStage(failedPaymentId) {
    try {
      const { data: failedPayment } = await supabase?.from('failed_payments')?.select('dunning_stage')?.eq('id', failedPaymentId)?.single();

      const stageProgression = {
        initial: 'reminder_1',
        reminder_1: 'reminder_2',
        reminder_2: 'final_notice',
        final_notice: 'suspended'
      };

      const nextStage = stageProgression?.[failedPayment?.dunning_stage] || 'suspended';

      // Update dunning stage
      await supabase?.from('failed_payments')?.update({ dunning_stage: nextStage })?.eq('id', failedPaymentId);

      // Send notification for new stage
      await this.sendDunningNotification(failedPaymentId, nextStage);

      return { data: { nextStage }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Send payment success notification
  async sendPaymentSuccessNotification(userId, amount) {
    try {
      const { data: user } = await supabase?.from('user_profiles')?.select('full_name, phone')?.eq('id', userId)?.single();

      if (user?.phone) {
        const message = `Great news ${user?.full_name}! Your payment of $${amount} has been processed successfully. Your membership is now active. Thank you!`;
        
        await twilioService?.sendSMS(
          user?.phone,
          message,
          'payment_success'
        );
      }

      return { data: { success: true }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get member churn risk data
  async getMemberChurnRisk(gymId, filters = {}) {
    try {
      let query = supabase?.from('member_churn_risk')?.select(`
          *,
          user:user_profiles!user_id(id, full_name, email, phone, subscription_plan)
        `)?.eq('gym_id', gymId)?.order('risk_score', { ascending: false });

      if (filters?.riskLevel) {
        query = query?.eq('risk_level', filters?.riskLevel);
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Trigger churn prevention campaign
  async triggerChurnPreventionCampaign(campaignId, targetUserIds) {
    try {
      const { data: campaign } = await supabase?.from('churn_prevention_campaigns')?.select('*')?.eq('id', campaignId)?.single();

      const interactions = [];

      for (const userId of targetUserIds) {
        const { data: user } = await supabase?.from('user_profiles')?.select('full_name, phone, email')?.eq('id', userId)?.single();

        // Personalize message
        let message = campaign?.message_template?.replace('{name}', user?.full_name);

        // Send SMS
        if (user?.phone) {
          const { data: smsResult } = await twilioService?.sendSMS(
            user?.phone,
            message,
            'churn_prevention',
            'churn_prevention_campaigns',
            campaignId
          );

          // Create interaction record
          const { data: interaction } = await supabase?.from('campaign_interactions')?.insert({
              campaign_id: campaignId,
              user_id: userId,
              notification_type: 'sms',
              sms_log_id: smsResult?.logId
            })?.select()?.single();

          interactions?.push(interaction);
        }
      }

      // Update campaign statistics
      await supabase?.from('churn_prevention_campaigns')?.update({ 
          total_sent: campaign?.total_sent + targetUserIds?.length 
        })?.eq('id', campaignId);

      return { data: interactions, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get payment recovery analytics
  async getPaymentRecoveryAnalytics(gymId, dateRange = {}) {
    try {
      let query = supabase?.from('payment_recovery_analytics')?.select('*')?.eq('gym_id', gymId)?.order('date', { ascending: false });

      if (dateRange?.startDate) {
        query = query?.gte('date', dateRange?.startDate);
      }

      if (dateRange?.endDate) {
        query = query?.lte('date', dateRange?.endDate);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Manual payment resolution
  async resolvePaymentManually(failedPaymentId, resolutionMethod, notes) {
    try {
      const { data, error } = await supabase?.from('failed_payments')?.update({
          is_resolved: true,
          resolved_at: new Date()?.toISOString(),
          resolution_method: resolutionMethod,
          notes: notes
        })?.eq('id', failedPaymentId)?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }
};